import '../../styles/LocationModal.scss';

import React, { PureComponent } from 'react';
import { Button, Grid } from 'semantic-ui-react';
import ResizeSensor from 'resize-sensor';
import Loader from '../../../components/loader';
import { getCurrentPosition, getMapImageUrl } from '../../../helpers/LocationUtility';
import { uploadFileFromUrl } from '../../../helpers/FileUtility';
import { rescaleImageInModal, getUriWithTimeStamp, uploadUri } from '../../../helpers/ImageUtility';
import { SasToken } from 'gf-types';
import { ValueChangedForFieldFunc } from '../../formeditor-types';
import GfModal from '../../../gfcomponents/GfModal/GfModal';

export interface LocationMapModalProps {
  w: number;
  h: number;
  value: string;
  name: string;
  sasToken: SasToken;
  formId: string;
  fontSize: number;
  color: string | { [name: string]: unknown };
  fontName: string;
  dateStamp: boolean;
  fieldId: string;
  rowNumber: number;
  fileUrls: Record<string, string>;
  valueUpdated?: ValueChangedForFieldFunc;
  onClose: VoidFunction;
  getSharedFormImageUrl: (fileId: string) => Promise<void>;
}

export default class LocationMapModal extends PureComponent<LocationMapModalProps, TypeMePlease> {
  private resizeSensor: ResizeSensor;
  private mapImgRef: HTMLElement;

  constructor(props) {
    super(props);

    this.handleDialogModalClose = this.handleDialogModalClose.bind(this);
    this.handleMapTypeRoadMapClicked = this.handleMapTypeRoadMapClicked.bind(this);
    this.handleMapTypeHybridClicked = this.handleMapTypeHybridClicked.bind(this);
    this.handleMapTypeTerrainClicked = this.handleMapTypeTerrainClicked.bind(this);
    this.handleZoomInClicked = this.handleZoomInClicked.bind(this);
    this.handleZoomOutClicked = this.handleZoomOutClicked.bind(this);
    this.handleGetLocationButtonClicked = this.handleGetLocationButtonClicked.bind(this);
    this.handleClearButtonClicked = this.handleClearButtonClicked.bind(this);
    this.handleCancelButtonClicked = this.handleCancelButtonClicked.bind(this);
    this.handleDoneButtonClicked = this.handleDoneButtonClicked.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);
    this.onMapLoaded = this.onMapLoaded.bind(this);
    this.createTimeStampUri = this.createTimeStampUri.bind(this);
    this.state = {
      zoom: 16,
      scale: 1,
      longitude: null,
      latitude: null,
      accuracy: null,
      maptype: 'roadmap',
      isLoading: false,
      showMapControls: false,
      locationMapSaving: false,
      scaledMapDimensions: null,
      imageUri: null
    };
  }

  componentDidMount() {
    if (!this.props.value) {
      this.getGpsLocation();
    }

    const d = document;
    const body = d.getElementsByTagName('body')[0];

    this.resizeSensor = new ResizeSensor(body, this.updateDimensions);
  }

  componentWillUnmount() {
    if (this.resizeSensor) {
      this.resizeSensor.detach();
    }
  }

  onMapLoaded() {
    this.updateDimensions();
  }

  getGpsLocation() {
    this.setState({ isLoading: true, showMapControls: false });

    getCurrentPosition({
      timeout: 10000, // 10 seconds
      maximumAge: 5 * 60000, // 5 minutes
      enableHighAccuracy: true
    })
      .then(({ coords }) => {
        this.setState(
          {
            zoom: 16,
            latitude: coords.latitude,
            longitude: coords.longitude,
            isLoading: false,
            accuracy: Math.round(coords.accuracy),
            showMapControls: true,
            error: null
          },
          () => {
            this.createTimeStampUri();
          }
        );
      })
      .catch(() => {
        this.setState({ latitude: null, longitude: null, error: 'Location not available', isLoading: false });
      });
  }

  getImageUrl() {
    const coords = {
      longitude: this.state.longitude,
      latitude: this.state.latitude,
      accuracy: this.state.accuracy
    };
    const dimensions = {
      scale: this.state.scale,
      zoom: this.state.zoom,
      w: this.props.w,
      h: this.props.h
    };

    return getMapImageUrl(coords, dimensions, this.state.maptype);
  }

  handleMapTypeRoadMapClicked() {
    this.setState({ maptype: 'roadmap' }, () => {
      this.createTimeStampUri();
    });
  }

  handleMapTypeHybridClicked() {
    this.setState({ maptype: 'hybrid' }, () => {
      this.createTimeStampUri();
    });
  }

  handleMapTypeTerrainClicked() {
    this.setState({ maptype: 'terrain' }, () => {
      this.createTimeStampUri();
    });
  }

  handleZoomInClicked() {
    if (this.state.zoom < 21) {
      this.setState({ zoom: this.state.zoom + 1 }, () => {
        this.createTimeStampUri();
      });
    }
  }

  handleZoomOutClicked() {
    if (this.state.zoom > 2) {
      this.setState({ zoom: this.state.zoom - 1 }, () => {
        this.createTimeStampUri();
      });
    }
  }

  async createTimeStampUri() {
    if (!this.props.dateStamp) {
      return;
    }
    const imageUrl = this.getImageUrl();
    const imageUri = await getUriWithTimeStamp(imageUrl, this.props.w, this.props.h, this.props.fontSize, this.props.fontName, this.props.color);
    this.setState({ imageUri });
  }

  //https://stackoverflow.com/questions/46387375/reactjs-get-latitude-on-click-and-show-it-in-input/46387466
  handleGetLocationButtonClicked() {
    this.getGpsLocation();
  }

  handleClearButtonClicked() {
    this.props.valueUpdated({ value: '' });
    this.props.onClose();
  }

  handleDialogModalClose() {
    this.props.onClose();
  }

  handleCancelButtonClicked() {
    this.props.onClose();
  }

  async handleDoneButtonClicked() {
    if (this.state.latitude && this.state.longitude) {
      const imageUrl = this.getImageUrl();
      if (imageUrl) {
        this.setState({ showMapControls: false, locationMapSaving: true });

        let fieldId = this.props.fieldId;
        if (this.props.rowNumber !== undefined) {
          fieldId += `_${this.props.rowNumber}`;
        }
        const { fileId, attachmentId } =
          this.props.dateStamp && this.state.imageUri
            ? await uploadUri(this.state.imageUri, this.props.w, this.props.h, this.props.formId, fieldId)
            : await uploadFileFromUrl(imageUrl, this.props.w, this.props.h, this.props.formId, fieldId);
        const newValue = {
          Latitude: this.state.latitude,
          Longitude: this.state.longitude,
          Accuracy: this.state.accuracy,
          AttachmentId: attachmentId,
          FileId: fileId
        };

        if (this.props.fileUrls) {
          await this.props.getSharedFormImageUrl(fileId);
        }

        this.props.valueUpdated({ value: JSON.stringify(newValue) });
      }
    } else if (this.state.error) {
      this.props.valueUpdated({ value: JSON.stringify({ Error: this.state.error }) });
    }

    this.props.onClose();
  }

  updateDimensions() {
    if (!this.mapImgRef) {
      return;
    }

    const { resizedWidth, resizedHeight } = rescaleImageInModal(this.props.w, this.props.h, (this.mapImgRef.parentNode.parentNode as HTMLElement).offsetWidth);

    this.setState({ scaledMapDimensions: { width: resizedWidth, height: resizedHeight } });
  }

  render() {
    const MIN_WIDTH_ALLOWED = 390;
    const SIDE_PADDING = 30;
    let image = null;
    let mapControls = null;
    let minModalWidth = this.props.w + SIDE_PADDING;
    let loading = null;
    let noGps = null;
    if (minModalWidth < MIN_WIDTH_ALLOWED) {
      minModalWidth = MIN_WIDTH_ALLOWED;
    }

    const scaledHeight = this.state.scaledMapDimensions ? this.state.scaledMapDimensions.height : this.props.h;
    const scaledWidth = this.state.scaledMapDimensions ? this.state.scaledMapDimensions.width : this.props.w;

    if (this.props.value) {
      const properties = JSON.parse(this.props.value);
      let imageUrl = null;
      if (this.props.fileUrls) {
        imageUrl = this.props.fileUrls[properties.FileId];
      } else if (properties.FileId) {
        imageUrl = `${this.props.sasToken.baseUrl}${properties.FileId}${this.props.sasToken.token}`;
      }

      if (imageUrl) {
        image = (
          <img
            src={imageUrl}
            alt="mapImage"
            width={scaledWidth}
            height={scaledHeight}
            ref={(r) => {
              this.mapImgRef = r;
            }}
            onLoad={this.onMapLoaded}
          />
        );
      }
    }

    if (this.state.zoom && this.state.longitude && this.state.latitude) {
      let imageUrl = null;
      if (this.state.imageUri) {
        imageUrl = this.state.imageUri;
      } else {
        imageUrl = this.getImageUrl();
      }
      image = (
        <img
          src={imageUrl}
          alt="mapImage"
          width={scaledWidth}
          height={scaledHeight}
          ref={(r) => {
            this.mapImgRef = r;
          }}
          onLoad={this.onMapLoaded}
        />
      );
    }

    if (this.state.isLoading) {
      image = null;
      loading = <Loader isLoading text="Acquiring Location" />;
    }

    if (this.state.locationMapSaving) {
      image = null;
      loading = <Loader isLoading text="Saving" />;
    }

    if (this.state.error) {
      noGps = <div>{this.state.error}</div>;
      image = null;
    }

    if (this.state.showMapControls) {
      mapControls = (
        <div>
          <Button.Group size="large">
            <Button onClick={this.handleZoomInClicked}>&#43;</Button>
            <Button onClick={this.handleZoomOutClicked}>&#8722;</Button>
            <Button onClick={this.handleMapTypeRoadMapClicked}>Map</Button>
            <Button onClick={this.handleMapTypeHybridClicked}>Sat</Button>
            <Button onClick={this.handleMapTypeTerrainClicked}>Terrain</Button>
          </Button.Group>
        </div>
      );
    }

    return (
      <GfModal
        title={this.props.name}
        className="location-modal"
        primaryText="Done"
        tertiaryText="Cancel"
        onPrimaryClick={this.handleDoneButtonClicked}
        onTertiaryClick={this.handleCancelButtonClicked}
        onClose={this.handleDialogModalClose}
        semanticUiModalProps={{ size: 'tiny', closeOnDimmerClick: true }}
      >
        <div className="toolbar-padding">
          <Grid>
            <Grid.Column floated="left" width={8}>
              <Button onClick={this.handleClearButtonClicked} className="clear" floated="left">
                Clear
              </Button>
            </Grid.Column>
            <Grid.Column floated="right" width={8}>
              <Button onClick={this.handleGetLocationButtonClicked} className="getlocation" floated="right">
                <i className="map pin icon" />
                Acquire Location
              </Button>
            </Grid.Column>
          </Grid>
        </div>
        {loading}
        {image}
        {mapControls}
        {noGps}
      </GfModal>
    );
  }
}
